import { Injectable, OnDestroy } from '@angular/core';
import { isCapacitorNative } from 'environments/environment-helpers';
import { LoggingService } from 'services/logging.service';
import { NavController } from 'providers/nav-controller';
import { PubSubRegistration, PubSubService, navigationStackChangedEvent } from 'services/pubsub.service';
import {
  AncestorStep,
  TAPPABLE_SELECTOR,
  countsAsVerticalScrollArea,
  describeControl,
  isPinnedToScreen,
  isStandingStill,
  nearestCustomElement,
  placeInContainer,
  reachesIntoBottomInset,
} from 'util/safe-area-overlap';

/** How long after a page change to look, so the page has finished animating in. */
const CHECK_AFTER_NAVIGATION_MS = 1500;

/**
 * How often to look again while the app is on screen. A page change is not the only moment a
 * control can land under the bar: the #5810 button only floated at the bottom edge once a search
 * had filled the list. Each look reads the position of every tappable control once, so it is
 * cheap, but not free — hence seconds, not frames.
 */
const PERIODIC_CHECK_MS = 15000;

/**
 * How long to wait before the second look. Longer than the 500ms slide of the tab bar and the
 * button bar (`$transition-slide-in-out`), so a control that was only passing the bottom edge
 * has stopped somewhere else by then (#6091).
 */
const SECOND_LOOK_AFTER_MS = 600;

/** One control found under the bar. */
export interface ControlUnderBottomInset {
  /** enclosing component, tag, data-e2e or id, first two classes and place — see describeControl */
  control: string;
  /** the control's bottom edge, in CSS pixels from the top of the screen */
  bottom: number;
  /** the height of the screen, in CSS pixels */
  viewportHeight: number;
  /** the height of the system navigation bar, in CSS pixels */
  insetBottom: number;
}

/**
 * Read one safe-area inset in CSS pixels, the way the stylesheets see it.
 *
 * The value is resolved by the browser through a throwaway element sized with the same
 * `var(--safe-area-inset-*, env(safe-area-inset-*, 0px))` chain `_mixins.scss` uses, because
 * the variable alone is empty on a browser that only reports `env()`.
 */
export function readSafeAreaInset(doc: Document, side: 'top' | 'bottom'): number {
  const probe = doc.createElement('div');
  probe.style.cssText = `position: fixed; visibility: hidden; pointer-events: none; top: 0; width: 0; ` +
    `height: var(--safe-area-inset-${side}, env(safe-area-inset-${side}, 0px));`;
  doc.body.appendChild(probe);
  const inset = probe.getBoundingClientRect().height;
  probe.remove();
  return inset;
}

/**
 * Every tappable control on screen that is pinned in place and reaches into the bottom inset.
 *
 * Also the body of the E2E test hook `testFindControlsUnderBottomInset`, so a test and the
 * production report can never disagree about what counts. This is one look; the production
 * check takes two and keeps only the controls that did not move (#6091).
 */
export function findControlsUnderBottomInset(doc: Document = document): ControlUnderBottomInset[] {
  return scanControlsUnderBottomInset(doc).map(seen => seen.finding);
}

/** One control found under the bar, with the element and box, so a second look can be compared. */
interface SeenControl {
  element: HTMLElement;
  box: { top: number; bottom: number };
  finding: ControlUnderBottomInset;
}

/** One look: every pinned tappable control under the bar, with its element and box. */
function scanControlsUnderBottomInset(doc: Document): SeenControl[] {
  const insetBottom = readSafeAreaInset(doc, 'bottom');
  const viewportHeight = doc.defaultView?.innerHeight ?? 0;
  if (insetBottom <= 0 || viewportHeight <= 0) {
    return [];
  }

  const found: SeenControl[] = [];
  // for each tappable control, cheapest test first: its box, then whether it is shown, then the walk up
  for (const element of Array.from(doc.querySelectorAll<HTMLElement>(TAPPABLE_SELECTOR))) {
    const box = element.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) {
      continue;
    }
    if (!reachesIntoBottomInset(box, viewportHeight, insetBottom)) {
      continue;
    }
    // a control inside another one (a cs-button's own <button>) is reported once, as the outer one
    if (element.parentElement?.closest(TAPPABLE_SELECTOR)) {
      continue;
    }
    // hidden pages stay in the DOM, so a control in one must not count
    if (typeof element.checkVisibility === 'function' &&
        !element.checkVisibility({ opacityProperty: true, visibilityProperty: true })) {
      continue;
    }
    if (!isPinnedToScreen(ancestorChain(element))) {
      continue;
    }
    const container = nearestCustomElement<Element>(element);
    found.push({
      element,
      box: { top: box.top, bottom: box.bottom },
      finding: {
        control: describeControl({
          tagName: element.tagName,
          id: element.id,
          dataE2e: element.getAttribute('data-e2e'),
          classNames: Array.from(element.classList),
          place: placeInContainer(element, container ?? doc.body),
          container: container && { tagName: container.tagName, classNames: Array.from(container.classList) },
        }),
        bottom: Math.round(box.bottom),
        viewportHeight,
        insetBottom,
      },
    });
  }
  return found;
}

/**
 * The control and each element above it, as `isPinnedToScreen` needs them. Which elements count
 * as scroll areas is decided by `countsAsVerticalScrollArea`.
 */
function ancestorChain(element: HTMLElement): AncestorStep[] {
  const chain: AncestorStep[] = [];
  // for each element from the control up to the root, record how it is positioned and whether it scrolls
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    chain.push({
      position: style.position,
      bottom: style.bottom,
      scrollsVertically: countsAsVerticalScrollArea({
        position: style.position,
        overflowX: style.overflowX,
        overflowY: style.overflowY,
        contentOverflows: node.scrollHeight > node.clientHeight,
      }),
    });
  }
  return chain;
}

/**
 * Reports tappable controls stuck under the system navigation bar on the native apps (#5810).
 *
 * Built once, from `app-wide-listeners.ts`, and never called into. On the web it does nothing:
 * a browser tab reports no bottom inset, so there is nothing to be under.
 *
 * Each control is reported once per page per session — a control under the bar stays there,
 * and one report is enough for a developer to find it.
 */
@Injectable({ providedIn: 'root' })
export class SafeAreaOverlapMonitorService implements OnDestroy {
  private readonly reported = new Set<string>();
  private navigationRegistration?: PubSubRegistration;
  private navigationTimer?: ReturnType<typeof setTimeout>;
  private periodicTimer?: ReturnType<typeof setInterval>;
  private secondLookTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private pubSub: PubSubService,
    private logging: LoggingService,
    private navCtrl: NavController,
  ) {
    if (!isCapacitorNative()) {
      return;
    }
    this.navigationRegistration = this.pubSub.subscribe(navigationStackChangedEvent, () => {
      // if pages change quickly, only the last one is worth looking at
      clearTimeout(this.navigationTimer);
      this.navigationTimer = setTimeout(() => this.check(), CHECK_AFTER_NAVIGATION_MS);
    });
    this.periodicTimer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.check();
      }
    }, PERIODIC_CHECK_MS);
  }

  ngOnDestroy() {
    this.navigationRegistration?.unsubscribe();
    clearTimeout(this.navigationTimer);
    clearInterval(this.periodicTimer);
    clearTimeout(this.secondLookTimer);
  }

  /**
   * Look once, and if anything is under the bar, look again a moment later. Only a control found
   * both times in the same place is reported: one caught in the middle of a slide (the tab bar
   * hiding, a page sliding in) has moved by the second look (#6091).
   */
  private check() {
    // if a second look is already waiting, it will report; a new pair would only repeat it
    if (this.secondLookTimer !== undefined) {
      return;
    }
    const firstLook = scanControlsUnderBottomInset(document);
    if (firstLook.length === 0) {
      return;
    }
    this.secondLookTimer = setTimeout(() => {
      this.secondLookTimer = undefined;
      this.reportStandingStill(firstLook, scanControlsUnderBottomInset(document));
    }, SECOND_LOOK_AFTER_MS);
  }

  /** Report each control found in the same place at both looks, once per page per session. */
  private reportStandingStill(firstLook: readonly SeenControl[], secondLook: readonly SeenControl[]) {
    const page = this.navCtrl.getActivePageName() ?? 'unknown';
    // for each control found the second time, keep it only if it was found in the same place the first time
    const standingStill = secondLook.filter(second => {
      const first = firstLook.find(seen => seen.element === second.element);
      return first !== undefined && isStandingStill(first.box, second.box);
    });
    // for each control found under the bar, report it unless it was already reported on this page
    for (const { finding } of standingStill) {
      const key = `${page} ${finding.control}`;
      if (this.reported.has(key)) {
        continue;
      }
      this.reported.add(key);
      this.logging.trackSilentProblem('ControlUnderSystemBar', { page, ...finding });
    }
  }
}
